"use client";

import { type FormEvent, useEffect, useRef, useState } from "react";
import { useModalDialog } from "@/hooks/useModalDialog";
import { apiRequest } from "@/lib/apiClient";
import type { AuthSession } from "@/lib/auth";

type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "error" };

const defaultAuthError = "Не удалось выполнить вход. Попробуйте ещё раз.";

const errorMessages: Record<string, string> = {
  access_not_granted:
    "Доступ для этого email ещё не открыт. Попросите администратора добавить вас.",
  email_link_invalid:
    "Ссылка для входа недействительна, истекла или уже была использована. Запросите новую.",
  yandex_cancelled: "Вход через Яндекс был отменён.",
  yandex_failed: "Яндекс не подтвердил вход. Попробуйте ещё раз.",
  yandex_invalid_flow: "Сессия входа истекла. Начните вход заново.",
  yandex_invalid_response: "Яндекс вернул неполный ответ. Попробуйте ещё раз.",
  yandex_invalid_state:
    "Не удалось подтвердить безопасность входа. Попробуйте ещё раз.",
  yandex_not_configured: "Вход через Яндекс пока не настроен.",
  google_cancelled: "Вход через Google был отменён.",
  google_failed: "Google не подтвердил вход. Попробуйте ещё раз.",
  google_invalid_flow: "Сессия входа истекла. Начните вход заново.",
  google_invalid_response: "Google вернул неполный ответ. Попробуйте ещё раз.",
  google_invalid_state:
    "Не удалось подтвердить безопасность входа. Попробуйте ещё раз.",
  google_not_configured: "Вход через Google пока не настроен.",
};

type LoginPanelProps = {
  returnTo?: string;
  errorCode?: string;
};

export function LoginPanel({ returnTo = "/login", errorCode }: LoginPanelProps) {
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });
  const providerError = errorCode === undefined
    ? null
    : (errorMessages[errorCode] ?? defaultAuthError);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [emailRequestState, setEmailRequestState] = useState<
    "idle" | "submitting" | "sent" | "error"
  >("idle");
  const emailInputRef = useRef<HTMLInputElement>(null);
  const emailDialogRef = useModalDialog<HTMLElement>({
    isOpen: emailDialogOpen,
    onClose: closeEmailDialog,
    initialFocusRef: emailInputRef,
  });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    async function loadSession() {
      try {
        const data = await apiRequest<AuthSession>("/api/auth/me", {
          signal: controller.signal,
        });
        if (!active) {
          return;
        }

        if (data.authenticated) {
          window.location.replace("/portal");
          return;
        }

        setAuth({ status: "anonymous" });
      } catch {
        if (active) {
          setAuth({ status: "error" });
        }
      }
    }

    void loadSession();
    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  function openEmailDialog() {
    setEmailRequestState("idle");
    setEmailDialogOpen(true);
  }

  function closeEmailDialog() {
    setEmailDialogOpen(false);
  }

  async function requestEmailLink(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setEmailRequestState("submitting");

    try {
      await apiRequest<void>("/api/auth/email", {
        method: "POST",
        json: {
          email,
          return_to: returnTo,
        },
      });

      setEmailRequestState("sent");
    } catch {
      setEmailRequestState("error");
    }
  }

  if (auth.status === "loading") {
    return (
      <div className="login-panel-status" role="status">
        <span className="login-status-dot" />
        Проверяем сессию…
      </div>
    );
  }

  return (
    <>
      <p className="login-panel-label">
        <span /> SSO
      </p>
      <h2>Один аккаунт для входа</h2>
      <p className="login-panel-copy">
        Выберите удобный аккаунт. Мы получим только данные, необходимые для
        входа.
      </p>

      {providerError && (
        <p className="login-error" role="alert">
          {providerError}
        </p>
      )}
      {auth.status === "error" && (
        <p className="login-error" role="alert">
          Не удалось проверить сессию. Обновите страницу и попробуйте ещё раз.
        </p>
      )}

      <div className="login-provider-buttons">
        <a
          className="login-provider-button"
          href={`/api/auth/yandex?return_to=${encodeURIComponent(returnTo)}`}
        >
          <span className="login-yandex-mark" aria-hidden="true">
            Я
          </span>
          <strong>Войти через Яндекс</strong>
          <b aria-hidden="true">→</b>
        </a>
        <a
          className="login-provider-button"
          href={`/api/auth/google?return_to=${encodeURIComponent(returnTo)}`}
        >
          <span className="login-google-mark" aria-hidden="true">
            G
          </span>
          <strong>Войти через Google</strong>
          <b aria-hidden="true">→</b>
        </a>
        <button
          className="login-provider-button login-email-provider-button"
          type="button"
          onClick={openEmailDialog}
        >
          <span className="login-email-mark" aria-hidden="true">
            @
          </span>
          <strong>Войти по e-mail</strong>
          <b aria-hidden="true">→</b>
        </button>
      </div>
      <p className="login-legal">
        Продолжая, вы подтверждаете вход выбранным способом.
      </p>

      {emailDialogOpen && (
        <div
          className="login-email-dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeEmailDialog();
            }
          }}
        >
          <section
            className="login-email-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="email-login-title"
            ref={emailDialogRef}
          >
            <button
              className="login-email-dialog-close"
              type="button"
              aria-label="Закрыть"
              onClick={closeEmailDialog}
            >
              ×
            </button>

            {emailRequestState === "sent" ? (
              <div className="login-email-dialog-result" role="status">
                <span aria-hidden="true">→</span>
                <h3 id="email-login-title">Проверьте почту</h3>
                <p>
                  Если этот e-mail добавлен в портал, мы отправили ссылку для
                  входа. Она действует 15 минут и сработает только один раз.
                </p>
                <button type="button" onClick={closeEmailDialog}>
                  Готово
                </button>
              </div>
            ) : (
              <>
                <p className="login-email-dialog-label">
                  Вход без пароля / e-mail
                </p>
                <h3 id="email-login-title">Получить ссылку для входа</h3>
                <p className="login-email-dialog-copy">
                  Укажите e-mail, добавленный администратором портала.
                </p>
                <form
                  className="login-email-form"
                  onSubmit={(event) => void requestEmailLink(event)}
                >
                  <label htmlFor="login-email">E-mail</label>
                  <input
                    ref={emailInputRef}
                    id="login-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    required
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      if (emailRequestState === "error") {
                        setEmailRequestState("idle");
                      }
                    }}
                    placeholder="name@example.com"
                  />
                  {emailRequestState === "error" && (
                    <p className="login-email-form-error" role="alert">
                      Не удалось отправить ссылку. Попробуйте ещё раз.
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={emailRequestState === "submitting"}
                  >
                    {emailRequestState === "submitting"
                      ? "Отправляем…"
                      : "Отправить ссылку"}
                    <span aria-hidden="true">→</span>
                  </button>
                </form>
              </>
            )}
          </section>
        </div>
      )}
    </>
  );
}
